/**
 * 组件注册表
 * 管理组件类型的位掩码分配
 */
export class ComponentRegistry {
    /**
     * 注册组件类型并分配位掩码
     * @param componentType 组件类型
     * @returns 分配的位索引
     */
    static register(componentType) {
        if (this.componentTypes.has(componentType)) {
            return this.componentTypes.get(componentType);
        }
        if (this.nextBitIndex >= this.maxComponents) {
            throw new Error(`Maximum number of component types (${this.maxComponents}) exceeded`);
        }
        const bitIndex = this.nextBitIndex++;
        this.componentTypes.set(componentType, bitIndex);
        return bitIndex;
    }
    /**
     * 获取组件类型的位掩码
     * @param componentType 组件类型
     * @returns 位掩码
     */
    static getBitMask(componentType) {
        const bitIndex = this.componentTypes.get(componentType);
        if (bitIndex === undefined) {
            throw new Error(`Component type ${componentType.name} is not registered`);
        }
        return BigInt(1) << BigInt(bitIndex);
    }
    /**
     * 获取组件类型的位索引
     * @param componentType 组件类型
     * @returns 位索引
     */
    static getBitIndex(componentType) {
        const bitIndex = this.componentTypes.get(componentType);
        if (bitIndex === undefined) {
            throw new Error(`Component type ${componentType.name} is not registered`);
        }
        return bitIndex;
    }
    /**
     * 检查组件类型是否已注册
     * @param componentType 组件类型
     * @returns 是否已注册
     */
    static isRegistered(componentType) {
        return this.componentTypes.has(componentType);
    }
    /**
     * 获取所有已注册的组件类型
     * @returns 组件类型映射
     */
    static getAllRegisteredTypes() {
        return new Map(this.componentTypes);
    }
}
ComponentRegistry.componentTypes = new Map();
ComponentRegistry.nextBitIndex = 0;
ComponentRegistry.maxComponents = 64; // 支持最多64种组件类型
/**
 * 高性能组件存储器
 * 使用SoA（Structure of Arrays）模式存储组件
 */
export class ComponentStorage {
    constructor(componentType) {
        this.components = [];
        this.entityToIndex = new Map();
        this.indexToEntity = [];
        this.freeIndices = [];
        this._size = 0;
        this.componentType = componentType;
        // 确保组件类型已注册
        if (!ComponentRegistry.isRegistered(componentType)) {
            ComponentRegistry.register(componentType);
        }
    }
    /**
     * 添加组件
     * @param entityId 实体ID
     * @param component 组件实例
     */
    addComponent(entityId, component) {
        // 检查实体是否已有此组件
        if (this.entityToIndex.has(entityId)) {
            throw new Error(`Entity ${entityId} already has component ${this.componentType.name}`);
        }
        let index;
        if (this.freeIndices.length > 0) {
            // 重用空闲索引
            index = this.freeIndices.pop();
            this.components[index] = component;
            this.indexToEntity[index] = entityId;
        }
        else {
            // 添加到末尾
            index = this.components.length;
            this.components.push(component);
            this.indexToEntity.push(entityId);
        }
        this.entityToIndex.set(entityId, index);
        this._size++;
    }
    /**
     * 获取组件
     * @param entityId 实体ID
     * @returns 组件实例或null
     */
    getComponent(entityId) {
        const index = this.entityToIndex.get(entityId);
        return index !== undefined ? this.components[index] : null;
    }
    /**
     * 检查实体是否有此组件
     * @param entityId 实体ID
     * @returns 是否有组件
     */
    hasComponent(entityId) {
        return this.entityToIndex.has(entityId);
    }
    /**
     * 移除组件
     * @param entityId 实体ID
     * @returns 被移除的组件或null
     */
    removeComponent(entityId) {
        const index = this.entityToIndex.get(entityId);
        if (index === undefined) {
            return null;
        }
        const component = this.components[index];
        this.entityToIndex.delete(entityId);
        this.components[index] = null;
        this.freeIndices.push(index);
        this._size--;
        return component;
    }
    /**
     * 高效遍历所有组件
     * @param callback 回调函数
     */
    forEach(callback) {
        for (let i = 0; i < this.components.length; i++) {
            const component = this.components[i];
            if (component) {
                callback(component, this.indexToEntity[i], i);
            }
        }
    }
    /**
     * 获取所有组件（密集数组）
     * @returns 组件数组
     */
    getDenseArray() {
        const components = [];
        const entityIds = [];
        for (let i = 0; i < this.components.length; i++) {
            const component = this.components[i];
            if (component) {
                components.push(component);
                entityIds.push(this.indexToEntity[i]);
            }
        }
        return { components, entityIds };
    }
    /**
     * 清空所有组件
     */
    clear() {
        this.components.length = 0;
        this.entityToIndex.clear();
        this.indexToEntity.length = 0;
        this.freeIndices.length = 0;
        this._size = 0;
    }
    /**
     * 获取组件数量
     */
    get size() {
        return this._size;
    }
    /**
     * 获取组件类型
     */
    get type() {
        return this.componentType;
    }
    /**
     * 压缩存储（移除空洞）
     */
    compact() {
        if (this.freeIndices.length === 0) {
            return; // 没有空洞，无需压缩
        }
        const newComponents = [];
        const newIndexToEntity = [];
        const newEntityToIndex = new Map();
        let newIndex = 0;
        for (let i = 0; i < this.components.length; i++) {
            const component = this.components[i];
            if (component) {
                newComponents[newIndex] = component;
                newIndexToEntity[newIndex] = this.indexToEntity[i];
                newEntityToIndex.set(this.indexToEntity[i], newIndex);
                newIndex++;
            }
        }
        this.components = newComponents;
        this.indexToEntity = newIndexToEntity;
        this.entityToIndex = newEntityToIndex;
        this.freeIndices.length = 0;
    }
    /**
     * 获取存储统计信息
     */
    getStats() {
        const totalSlots = this.components.length;
        const usedSlots = this._size;
        const freeSlots = this.freeIndices.length;
        const fragmentation = totalSlots > 0 ? freeSlots / totalSlots : 0;
        return {
            totalSlots,
            usedSlots,
            freeSlots,
            fragmentation
        };
    }
}
/**
 * 组件存储管理器
 * 管理所有组件类型的存储器
 */
export class ComponentStorageManager {
    constructor() {
        this.storages = new Map();
    }
    /**
     * 获取或创建组件存储器
     * @param componentType 组件类型
     * @returns 组件存储器
     */
    getStorage(componentType) {
        let storage = this.storages.get(componentType);
        if (!storage) {
            storage = new ComponentStorage(componentType);
            this.storages.set(componentType, storage);
        }
        return storage;
    }
    /**
     * 添加组件
     * @param entityId 实体ID
     * @param component 组件实例
     */
    addComponent(entityId, component) {
        const componentType = component.constructor;
        const storage = this.getStorage(componentType);
        storage.addComponent(entityId, component);
    }
    /**
     * 获取组件
     * @param entityId 实体ID
     * @param componentType 组件类型
     * @returns 组件实例或null
     */
    getComponent(entityId, componentType) {
        const storage = this.storages.get(componentType);
        return storage ? storage.getComponent(entityId) : null;
    }
    /**
     * 检查实体是否有组件
     * @param entityId 实体ID
     * @param componentType 组件类型
     * @returns 是否有组件
     */
    hasComponent(entityId, componentType) {
        const storage = this.storages.get(componentType);
        return storage ? storage.hasComponent(entityId) : false;
    }
    /**
     * 移除组件
     * @param entityId 实体ID
     * @param componentType 组件类型
     * @returns 被移除的组件或null
     */
    removeComponent(entityId, componentType) {
        const storage = this.storages.get(componentType);
        return storage ? storage.removeComponent(entityId) : null;
    }
    /**
     * 移除实体的所有组件
     * @param entityId 实体ID
     */
    removeAllComponents(entityId) {
        for (const storage of this.storages.values()) {
            storage.removeComponent(entityId);
        }
    }
    /**
     * 获取实体的组件位掩码
     * @param entityId 实体ID
     * @returns 组件位掩码
     */
    getComponentMask(entityId) {
        let mask = BigInt(0);
        for (const [componentType, storage] of this.storages.entries()) {
            if (storage.hasComponent(entityId)) {
                mask |= ComponentRegistry.getBitMask(componentType);
            }
        }
        return mask;
    }
    /**
     * 压缩所有存储器
     */
    compactAll() {
        for (const storage of this.storages.values()) {
            storage.compact();
        }
    }
    /**
     * 获取所有存储器的统计信息
     */
    getAllStats() {
        const stats = new Map();
        for (const [componentType, storage] of this.storages.entries()) {
            const typeName = componentType.name || 'Unknown';
            stats.set(typeName, storage.getStats());
        }
        return stats;
    }
    /**
     * 清空所有存储器
     */
    clear() {
        for (const storage of this.storages.values()) {
            storage.clear();
        }
        this.storages.clear();
    }
}
//# sourceMappingURL=ComponentStorage.js.map